import styled from '@emotion/styled';
import { useState } from 'react';
import { CourseCard } from './components/CourseCard';
import { useStudent } from '../context/StudentContext';
import { getEnrolledCourses } from '../utils';
import { screenSizes } from 'src/assets/styles/Literals';

const DashboardContainer = styled.div`
  width: 100%;
  max-width: ${screenSizes.desktop};
  margin: 0 auto;
  padding: 1rem;
  background-color: #f8f9fa;
  min-height: 100vh;

  @media (min-width: ${screenSizes.tablet}) {
    padding: 2rem;
  }
`;

const Header = styled.div`
  padding: 1rem;
`;

const Title = styled.h1`
  font-family: 'Raleway', sans-serif;
  font-size: 1.8rem;
  color: #2c3e50;
  margin: 0;
  font-weight: 700;
  text-align: center;

  @media (min-width: ${screenSizes.tablet}) {
    font-size: 2.5rem;
    text-align: left;
  }
`;

const WelcomeMessage = styled.p`
  font-family: 'Raleway', sans-serif;
  font-size: 1.1rem;
  color: #3e3e3e;
  margin: 0;
  padding: 0;
  font-weight: 400;
`;

const Section = styled.section`
  background: white;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  @media (min-width: ${screenSizes.tablet}) {
    padding: 2rem;
    margin-bottom: 2rem;
  }
`;

const SectionTitle = styled.h2`
  font-family: 'Raleway', sans-serif;
  font-size: 1.4rem;
  color: #2c3e50;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e9ecef;

  @media (min-width: ${screenSizes.tablet}) {
    font-size: 1.8rem;
    margin-bottom: 1.5rem;
  }
`;

const CourseGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-top: 1rem;

  @media (min-width: ${screenSizes.mobile}) {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1.5rem;
  }

  @media (min-width: ${screenSizes.tablet}) {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 2rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem 1rem;
  color: #6c757d;
  font-size: 1rem;

  @media (min-width: ${screenSizes.tablet}) {
    padding: 3rem;
    font-size: 1.1rem;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.5rem;

  @media (min-width: ${screenSizes.tablet}) {
    flex-direction: row;
    gap: 1rem;
    margin-bottom: 2rem;
  }
`;

const Tab = styled.button`
  padding: 0.75rem 1rem;
  font-size: 1rem;
  font-weight: 600;
  color: ${(props) => (props.active ? '#2c3e50' : '#6c757d')};
  background: ${(props) => (props.active ? 'white' : 'transparent')};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${(props) => (props.active ? '0 2px 4px rgba(0, 0, 0, 0.05)' : 'none')};
  text-align: center;

  @media (min-width: ${screenSizes.tablet}) {
    padding: 1rem 2rem;
    font-size: 1.1rem;
    text-align: left;
  }

  &:hover {
    color: #2c3e50;
    background: ${(props) => (props.active ? 'white' : 'rgba(255, 255, 255, 0.5)')};
  }
`;

const Dashboard = ({ courses, getStudentProgress } = []) => {
  const [activeTab, setActiveTab] = useState('enrolled');
  const { student } = useStudent();
  const enrolledCourses = getEnrolledCourses(student, courses);

  return (
    <DashboardContainer>
      <Header>
        <Title onClick={() => getStudentProgress({ identification: student.identification })}>
          Mi Aprendizaje
        </Title>
        <WelcomeMessage>Bienvenido, {student.name}</WelcomeMessage>
      </Header>

      <TabsContainer>
        <Tab active={activeTab === 'enrolled'} onClick={() => setActiveTab('enrolled')}>
          Mis Cursos
        </Tab>
        <Tab active={activeTab === 'catalog'} onClick={() => setActiveTab('catalog')}>
          Catálogo de Cursos
        </Tab>
      </TabsContainer>

      {activeTab === 'enrolled' ? (
        <Section>
          <SectionTitle>Mis Cursos</SectionTitle>
          {enrolledCourses?.length > 0 ? (
            <CourseGrid>
              {enrolledCourses.map((course) => (
                <CourseCard key={course.id} course={course} />
              ))}
            </CourseGrid>
          ) : (
            <EmptyState>
              Aún no estás inscrito en ningún curso. ¡Explora nuestro catálogo y comienza tu
              aprendizaje!
            </EmptyState>
          )}
        </Section>
      ) : (
        <Section>
          <SectionTitle>Catálogo de Cursos</SectionTitle>
          <CourseGrid>
            {courses.map((course) => (
              <CourseCard key={course.id} course={course} />
            ))}
          </CourseGrid>
          {courses?.filter((course) => !enrolledCourses.some((c) => c.id === course.id))?.length ===
            0 && <EmptyState>No hay más cursos disponibles</EmptyState>}
        </Section>
      )}
    </DashboardContainer>
  );
};

export { Dashboard };
